import React, { useCallback, useRef } from "react";
import styled from "@emotion/styled";

import { varColor } from "../../utils/themeHelpers";
import { BaseGroupButton } from "./BaseGroupButton";
import { IPickerLink, PickerItem } from "./Picker.types";
import { SelectionCanceller } from "./SelectionCanceller";
import { useSelectedFocusManager } from "./useSelectedFocusManager";

interface IItemLinkProps<TTab extends string, TGroup extends string> {
    item: IPickerLink<TTab, TGroup>;
    selected: boolean;
    setIndexedItem: (item: PickerItem<TTab, TGroup>) => void;
    onSelect?: (item: IPickerLink<TTab, TGroup>) => void;
}

const GroupLink = styled(BaseGroupButton)`
    margin-inline: ${12 / 16}rem;
    color: ${varColor("highlights", "blue")};

    &:hover {
        background-color: ${varColor("primary", "steel02")};
    }

    &:focus-within {
        background-color: ${varColor("primary", "steel03")};
    }
`;

export function ItemLink<TTab extends string, TGroup extends string>(
    props: IItemLinkProps<TTab, TGroup>,
) {
    const { item, selected, setIndexedItem, onSelect } = props;
    const { link } = item;
    const setIndex = useCallback(
        () => setIndexedItem(item),
        [setIndexedItem, item],
    );

    const linkRef = useRef<HTMLButtonElement>(null);

    useSelectedFocusManager(selected, linkRef, linkRef);

    const handleClick = useCallback(() => {
        const canceller = new SelectionCanceller();
        item.onClick?.call(item, canceller);
        if (!canceller.isCancelled) {
            onSelect?.(item);
        }
    }, [item]);

    return (
        <GroupLink
            ref={linkRef}
            onClick={handleClick}
            selected={selected}
            onFocus={setIndex}
        >
            {link}…
        </GroupLink>
    );
}
